import { useQueryClient } from 'react-query';
import { RecoilState, atom, selector, useRecoilState, useRecoilValue } from 'recoil';
import { getRecoil, resetRecoil } from 'recoil-nexus';
import { env } from './common';
import { MiningAccount, MiningPool, loadBalancerAPI } from './load-balancer';
import { AlertState } from '../abelcommon';
import { MachineStatus } from '../app/Account/MachinePage/components/MachineListSection';

export interface AuthState {
  user?: {
    id: string;
    name: string;
    email: string;
    role: number;
    languageCode: string;
    twoFAState?: boolean;
  } | null;
  accessToken?: string | null;
  refreshToken?: string | null;
  isLoading?: boolean;
}

export type UserState = {
  poolname?: string;
  username?: string;
  signinAt?: number;
  token?: string;
  isLoading?: boolean;
};

export type UserStates = {
  [key: string]: UserState;
};

const storageKeyPrefix = 'abelian';
const themeStorageKey = `stable-${storageKeyPrefix}:theme`;
export const userStorageKey = `${storageKeyPrefix}:current_mabel_user`;
const currentMiningAccountStorageKey = `${storageKeyPrefix}:current_mining_account`;
const appVersionStorageKey = `stable-${storageKeyPrefix}:app_version`;

export const clearAllStorage = () => {
  const prefixedStorageKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(storageKeyPrefix)) {
      prefixedStorageKeys.push(key);
    }
  }

  for (const key of prefixedStorageKeys) {
    localStorage.removeItem(key);
  }
};

export const resetRecoilState = () => {
  resetRecoil(authState);
  resetRecoil(selectedMiningAccountState);
  const userStatesValue = getRecoil(userStates);
  Object.values(userStatesValue).forEach((userState) => resetRecoil(userState));
  resetRecoil(switchAccountMenuOpenState);
};

export const themeState = atom({
  key: 'themeState',
  default: !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  effects: [
    ({ setSelf, onSet }) => {
      const savedValue = localStorage.getItem(themeStorageKey);
      if (savedValue) {
        setSelf(savedValue);
      }

      onSet((newValue, _, isReset) => {
        isReset ? localStorage.removeItem(themeStorageKey) : localStorage.setItem(themeStorageKey, newValue);
      });
    },
  ],
});

export const miningPoolsState = atom<MiningPool[]>({
  key: 'miningPoolsState',
  default: loadBalancerAPI.apiInstance
    .getListMiningPools()
    .then((response) => response.data.data)
    .catch(() => {
      console.error('Failed to load mining pools');
      return [];
    }),
});

export const authState = atom<AuthState>({
  key: 'auth',
  default: {
    user: null,
    accessToken: null,
    refreshToken: null,
    isLoading: false,
  },
  effects: [
    ({ setSelf, onSet }) => {
      const getValueAsync = async () => {
        const savedValue = localStorage.getItem(userStorageKey);
        if (savedValue) {
          try {
            const parsedValue = JSON.parse(savedValue);
            if (!parsedValue?.refreshToken) return;
            setSelf((prev) => ({ ...prev, isLoading: true }));
            const { data } = await loadBalancerAPI.apiInstance.refreshTokenWithUserInfo(parsedValue.refreshToken);
            setSelf(data.data);
          } catch {
            setSelf((prev) => ({ ...prev, isLoading: false }));
          }
        }
      };

      getValueAsync();

      onSet((newValue, _, isReset) => {
        isReset
          ? localStorage.removeItem(userStorageKey)
          : localStorage.setItem(
              userStorageKey,
              JSON.stringify({
                userId: newValue.user?.id,
                refreshToken: newValue.refreshToken,
              })
            );
      });
    },
  ],
});

export const selectedMiningAccountState = atom<MiningAccount>({
  key: 'selectedMiningAccountState',
  default: null,
  effects: [
    ({ setSelf, onSet }) => {
      const savedValue = localStorage.getItem(currentMiningAccountStorageKey);
      try {
        if (savedValue) {
          const parsedValue = JSON.parse(savedValue);
          if (parsedValue.id && parsedValue.name && parsedValue.username) {
            setSelf({
              id: parsedValue.id,
              name: parsedValue.name,
              username: parsedValue.username,
              walletAddress: parsedValue.walletAddress,
              password: parsedValue.password,
            });
          }
        }
      } catch (error) {
        console.error('Failed to load selected mining account');
      }

      onSet((newValue, _, isReset) => {
        isReset
          ? localStorage.removeItem(currentMiningAccountStorageKey)
          : localStorage.setItem(
              currentMiningAccountStorageKey,
              JSON.stringify({
                id: newValue.id,
                name: newValue.name,
                username: newValue.username,
                walletAddress: newValue.walletAddress,
                password: newValue.password,
              })
            );
      });
    },
  ],
});

export const userStates = selector({
  key: 'userStates',
  get: ({ get }) => {
    const listMiningPools = get(miningPoolsState);
    return Object.fromEntries(
      listMiningPools.map<[string, RecoilState<UserState>]>(({ name: poolname }) => [
        poolname,
        atom<UserState>({
          key: `pool-user-state-${poolname}`,
          default: { poolname: poolname, username: null, signinAt: null, token: null, isLoading: false },
          effects: [
            ({ setSelf, onSet, resetSelf }) => {
              const storageKey = `${storageKeyPrefix}:pool-user-state-${poolname}`;
              onSet((newValue, _, isReset) => {
                isReset
                  ? localStorage.removeItem(storageKey)
                  : localStorage.setItem(storageKey, JSON.stringify(newValue));
              });

              try {
                const savedUserState = localStorage.getItem(storageKey);
                const savedCurrentMiningAccount = localStorage.getItem(currentMiningAccountStorageKey);
                if (savedUserState !== null) {
                  if (!savedCurrentMiningAccount) {
                    resetSelf();
                  } else {
                    const parsedUserState = JSON.parse(savedUserState);
                    const parsedCurrentMiningAccount = JSON.parse(savedCurrentMiningAccount);
                    if (parsedUserState.token && parsedUserState.username !== parsedCurrentMiningAccount.username) {
                      resetSelf();
                    } else {
                      setSelf(parsedUserState);
                    }
                  }
                }
              } catch {
                console.error('Failed to load user state');
              }

              const storageListener = (e: StorageEvent) => {
                if (e.storageArea === localStorage && e.key === storageKey) {
                  setSelf(e.newValue ? JSON.parse(e.newValue) : e.newValue);
                }
              };
              window.addEventListener('storage', storageListener);
            },
          ],
        }),
      ])
    );
  },
});

export const switchAccountMenuOpenState = atom({
  key: 'switchAccountMenuOpenState',
  default: true,
});

export const globalAlertState = atom<AlertState>({
  key: 'globalAlertState',
  default: {
    isOpen: false,
    message: '',
    severity: 'info',
    autoHideDuration: 6000,
  },
});

export function useLogout() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.removeQueries();
    resetRecoilState();
    clearAllStorage();
  };
}

export function useUserState(poolname?: string) {
  const userState = useRecoilValue(userStates);
  const miningPoolValues = useRecoilValue(miningPoolsState);
  const userStateAtom = userState[poolname ?? env.defaultPoolname] ?? userState[miningPoolValues[0].name];
  return useRecoilState(userStateAtom);
}

export function useSignedinUserStateValues() {
  const userState = useRecoilValue(userStates);
  const miningPoolValues = useRecoilValue(miningPoolsState);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const userStateValues = miningPoolValues.map(({ name: pn }) => useRecoilValue<UserState>(userState[pn]));
  return userStateValues.filter((us: UserState) => Boolean(us.token));
}

export function useSignedinPoolnames() {
  const signedinUserStateValues = useSignedinUserStateValues();
  return signedinUserStateValues.map((v: UserState) => v.poolname);
}

export function useUsername() {
  const signedinUserStateValues = useSignedinUserStateValues();
  return signedinUserStateValues.length > 0 ? signedinUserStateValues[0].username : null;
}

export function useUserToken(poolname: string) {
  const userState = useRecoilValue(userStates);
  const state = useRecoilValue<UserState>(userState[poolname ?? env.defaultPoolname]);
  return state.token ?? '';
}

export function useUserSignedIn() {
  const authStateValue = useRecoilValue(authState);
  return { isLoading: authStateValue.isLoading, isSignedIn: !!authStateValue.refreshToken };
}

export function useAdminAccountSignedIn() {
  const authStateValue = useRecoilValue(authState);
  const isAdmin = authStateValue.user?.role === 1;
  return { isLoading: authStateValue.isLoading, isSignedIn: isAdmin && !!authStateValue.refreshToken };
}

export function useContentManagerAccountSignedIn() {
  const authStateValue = useRecoilValue(authState);
  const isContentManager = authStateValue.user?.role === 2;
  return { isLoading: authStateValue.isLoading, isSignedIn: isContentManager && !!authStateValue.refreshToken };
}

export function useMiningAccountSignedIn() {
  const miningPoolValues = useRecoilValue(miningPoolsState);
  const userStatesValue = useRecoilValue(userStates);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const userInnerStates = miningPoolValues.map(({ name: pn }) => useRecoilValue(userStatesValue[pn]));
  return {
    isLoading: userInnerStates.some(({ isLoading }) => isLoading),
    isSignedIn: userInnerStates.some(({ token }) => token),
  };
}

export function usePoolSignedIn(poolname: string) {
  const token = useUserToken(poolname);
  return Boolean(token);
}

const filterMachineStorageKey = `stable-${storageKeyPrefix}:filter-machine`;
export const machineState = atom({
  key: 'machineState',
  default: MachineStatus.All,
  effects: [
    ({ setSelf, onSet }) => {
      const savedValue: MachineStatus = localStorage.getItem(filterMachineStorageKey) as MachineStatus;
      if (savedValue) {
        setSelf(savedValue);
      }

      onSet((newValue, _, isReset) => {
        isReset
          ? localStorage.removeItem(filterMachineStorageKey)
          : localStorage.setItem(filterMachineStorageKey, newValue);
      });
    },
  ],
});

export const getAppVersion = () => localStorage.getItem(appVersionStorageKey);
export const persistAppVersion = (version: string) => localStorage.setItem(appVersionStorageKey, version);

export const appVersionState = atom({
  key: 'appVersionState',
  default: {
    isStale: false,
    appVersion: getAppVersion(),
  },
});
